import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';

export type GetCurrenciesResponse = {
  // for consumer needing direct access to the data of a currency
  dict: Record<Currency, CurrencyData>;
  // for consumer needing memoized customized list of currency codes
  // RTK query provides memoization via its cache
  list: CurrencyPair[];
};

// FAKE (not the same behavior in production)
// - EUR/USD -> not NDF by default, but editable
// - EUR/CNY -> NDF not editable
// - GBP/QAR -> not visible

/**
 * | pair    | cash NDF                               | isInverted | canBeDelivered | notDeliverable | isCashDeliverable | isOptionCurrency |
 * |---------|----------------------------------------|------------|----------------|----------------|-------------------|------------------|
 * | EUR/USD | visible / editable / default unchecked | false      | true           | false          | true              | true             |
 * | USD/EUR | invisible                              | true       | true           | false          | false             | false            |
 * | EUR/CNY | visible / fixed / checked              | false      | false          | true           | false             | false            |
 * | GBP/QAR | invisible                              | false      | false          | false          | false             | false            |
 * | AUD/KZT | visible / editable / default checked   | false      | true           | true           | false             | false            |
 * | USD/RUB | visible / editable / default unchecked | false      | true           | true           | false             | true            |
 * */

export type CurrencyData = {
  pair: CurrencyPair;
  ccy1: Currency;
  ccy2: Currency;
  precision: number;

  // an inverted currency pair exists, and the other one is preferred
  // NDF checkbox is not editable and not visible
  isInverted: boolean;

  // NDF checkbox is visible and editable if true and not inverted
  canBeDelivered: boolean;

  // NDF checkbox is visible if true and not inverted
  notDeliverable: boolean;

  isCashDeliverable: boolean;

  isOptionCurrency: boolean;
};

export const getCurrencies = (builder: WebapiEndpointBuilder) => {
  return builder.query<GetCurrenciesResponse, void>({
    keepUnusedDataFor: Number.POSITIVE_INFINITY,

    query: () => {
      return {
        url: `${window.location.origin}/currencies.json`,
        method: 'GET',
      };
    },

    transformResponse: (response: CurrencyData[]): GetCurrenciesResponse => {
      const dict = Object.fromEntries(response.map((currencyData) => [currencyData.pair, currencyData])) as Record<
        CurrencyPair,
        CurrencyData
      >;

      const list = Object.keys(dict) as CurrencyPair[];

      return { dict, list };
    },
  });
};
